import React, { useEffect, useState } from 'react';
import './App.scss';

import { Container, Row, Col, Button, Modal } from 'react-bootstrap';
import ContactForm from './components/contact-form';
import Slideshow from './components/slideshow';

type SectionId = 'achtergrond' | 'fotogalerij' | 'privacy-respect' | 'samenwerking';

const SECTION_PATHS: Record<SectionId, string> = {
  achtergrond: '/achtergrond',
  fotogalerij: '/fotogalerij',
  'privacy-respect': '/privacy-respect',
  samenwerking: '/samenwerking'
};

const normalizePath = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

const getSectionIdFromLocation = (): SectionId | null => {
  const hash = window.location.hash.replace(/^#/, '');
  if (hash && hash in SECTION_PATHS) {
    return hash as SectionId;
  }

  const pathname = normalizePath(window.location.pathname);
  const matchedEntry = (Object.entries(SECTION_PATHS) as Array<[SectionId, string]>).find(
    ([, path]) => path === pathname
  );

  return matchedEntry?.[0] ?? null;
};

const updateHistory = (sectionId: SectionId | null, replace = false) => {
  const nextUrl = sectionId ? SECTION_PATHS[sectionId] : '/';

  if (replace) {
    window.history.replaceState(window.history.state, '', nextUrl);
    return;
  }

  window.history.pushState(window.history.state, '', nextUrl);
};

const scrollToSection = (
  sectionId: SectionId,
  options?: { behavior?: ScrollBehavior; replaceHistory?: boolean }
) => {
  const element = document.getElementById(sectionId);
  if (element) {
    updateHistory(sectionId, options?.replaceHistory ?? false);
    element.scrollIntoView({ behavior: options?.behavior ?? 'smooth' });
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'scroll_to_section', {
        section_name: sectionId,
        event_category: 'navigation'
      });
    }
  }
};

const scrollToTop = (options?: { behavior?: ScrollBehavior; replaceHistory?: boolean }) => {
  updateHistory(null, options?.replaceHistory ?? false);
  window.scrollTo({ top: 0, behavior: options?.behavior ?? 'smooth' });
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'scroll_to_top', {
      event_category: 'navigation'
    });
  }
};

const App: React.FC = () => { 
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const videoElement = document.querySelector('video');
    if (videoElement) {
      const playVideo = () => {
        videoElement.play().catch(() => {
          alert('Failed to play video');
        });
      };
      videoElement.addEventListener('canplay', playVideo);

      return () => {
        videoElement.removeEventListener('canplay', playVideo);
      };
    }
  }, []);

  useEffect(() => {
    const syncRouteWithScrollPosition = () => {
      const sectionId = getSectionIdFromLocation();

      if (window.location.hash && sectionId) {
        updateHistory(sectionId, true);
      }

      if (sectionId) {
        window.requestAnimationFrame(() => {
          scrollToSection(sectionId, { behavior: 'auto', replaceHistory: true });
        });
        return;
      }

      if (normalizePath(window.location.pathname) !== '/') {
        updateHistory(null, true);
      }

      window.requestAnimationFrame(() => {
        scrollToTop({ behavior: 'auto', replaceHistory: true });
      });
    };

    syncRouteWithScrollPosition();
    window.addEventListener('popstate', syncRouteWithScrollPosition);

    return () => {
      window.removeEventListener('popstate', syncRouteWithScrollPosition);
    };
  }, []);

  const trackSocialClick = (platform: string) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'social_click', {
        platform: platform,
        event_category: 'social_media'
      });
    }
  };

  return (
    <div className="App">
      <p className="visually-hidden">Lucht van Heerde - Dronefotografie en Video's van Heerde en Omgeving</p>
      <video autoPlay loop muted playsInline className="video-background" poster='https://lvhpswasa.blob.core.windows.net/public/golden-hour/background.jpg'>
        <source src="https://lvhpswasa.blob.core.windows.net/public/golden-hour/background.webm" type="video/webm" />
        <source src="https://lvhpswasa.blob.core.windows.net/public/golden-hour/background.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Hero Section */}
      <header className="hero-section">
        <Container className="h-100 d-flex align-items-center justify-content-center">
          <Row>
            <Col className="text-center">
              <div className="logo-container">
                <svg className="logo" viewBox="0 0 1461 726" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Lucht van Heerde logo">
                  <g transform="matrix(1,0,0,1,-40.2812,-168.040473)">
                    <g transform="matrix(1.591063,0,0,1.591063,-453.137462,-312.453167)">
                      <path className="logo-black" d="M714.197,539.107C732.644,527.876 752.43,521.719 774.255,523C789.125,523.873 802.822,528.55 816.468,535.716C829.475,543.829 839.978,553.652 848.762,566.281C926.192,494.423 970.744,406.141 985.013,302.168L1030.854,302.168L1030.854,447.51L1110.676,447.51C1110.756,445.917 1110.919,444.162 1110.92,442.408C1110.937,401.908 1110.943,361.409 1110.931,320.909C1110.93,314.744 1111.462,314.145 1117.828,314.142C1151.662,314.128 1185.495,314.132 1219.328,314.125C1227.492,314.123 1228.188,314.709 1228.195,322.873C1228.239,369.039 1228.253,415.206 1228.241,461.372C1228.225,523.038 1228.191,584.704 1228.127,646.37C1228.115,657.054 1226.009,659.485 1215.319,659.54C1184.488,659.7 1153.655,659.561 1122.823,659.512C1121.501,659.51 1120.173,659.269 1118.857,659.079C1112.222,658.119 1110.95,656.752 1110.933,650.016C1110.881,629.35 1110.896,608.683 1110.89,588.017C1110.886,574.036 1110.889,560.054 1110.889,545.669C1108.818,545.568 1107.025,545.407 1105.233,545.405C1082.233,545.386 1059.232,545.49 1036.234,545.298C1031.972,545.263 1030.948,546.676 1030.968,550.742C1031.113,581.741 1031.037,612.741 1031.031,643.741C1031.029,657.296 1029.094,659.469 1015.496,659.541C991.665,659.669 967.829,659.248 944.003,659.608C930.726,659.809 917.362,660.344 904.224,662.118C885.793,664.605 874.178,676.048 868.114,693.339C865.032,702.129 862.043,710.954 859.109,719.796C853.098,737.909 840.606,748.474 821.988,752.181C785.089,759.529 748.273,760.122 711.549,751.083C696.153,747.294 685.611,737.82 680.063,723.105C676.96,714.877 674.289,706.435 672.132,697.911C666.436,675.397 651.854,662.968 629.148,661.146C610.919,659.683 592.549,659.61 574.24,659.583C492.91,659.458 411.58,659.576 330.25,659.585C327.759,659.585 325.251,659.504 322.78,659.212C313.193,658.077 311.115,655.932 310.242,646.38C310.075,644.56 310.126,642.717 310.126,640.885C310.137,536.052 310.153,431.22 310.168,326.387C310.168,325.387 310.142,324.386 310.171,323.387C310.396,315.588 311.92,314.129 319.714,314.13C353.714,314.134 387.714,314.129 421.713,314.126C429.09,314.125 429.176,314.2 429.177,321.744C429.18,391.243 429.18,460.743 429.182,530.242L429.182,536.431L587.88,536.431C560.558,477.57 533.462,419.197 505.741,359.475C522.045,359.475 537.071,359.475 552.648,359.869C586.778,436.238 630.372,505.69 686.43,565.852C695.855,556.775 705.026,547.941 714.197,539.107M732.189,672.084C758.214,690.498 799.09,685.272 820.44,660.802C842.405,635.628 842.229,597.291 819.678,573.223C808.182,560.954 794.247,552.982 776.229,551.672C755.96,549.608 738.228,555.126 722.93,568.819C691.026,597.378 695.414,649.61 732.189,672.084Z"/>
                    </g>
                    <g transform="matrix(1.591063,0,0,1.591063,-453.137462,-312.453167)">
                      <path className="logo-yellow" d="M984.552,302.081C970.744,406.141 926.192,494.423 848.762,566.281C839.978,553.652 829.475,543.829 816.804,535.847C843.302,501.677 861.91,463.589 874.348,422.404C883.427,392.344 889.109,361.626 891.112,330.262C891.705,320.99 892.202,311.712 892.769,301.995C923.485,301.995 953.788,301.995 984.552,302.081Z"/>
                    </g>
                    <g transform="matrix(1.591063,0,0,1.591063,-453.137462,-312.453167)">
                      <path className="logo-yellow" d="M714.039,538.788C705.026,547.941 695.855,556.775 686.43,565.852C630.372,505.69 586.778,436.238 553.108,359.959C557.988,359.489 562.959,359.191 567.93,359.184C587.063,359.158 606.196,359.195 625.328,359.275C632.228,359.304 633.577,360.392 635.789,366.848C654.844,422.467 677.804,476.353 707.101,527.423C709.249,531.168 711.615,534.789 714.039,538.788Z"/>
                    </g>
                    <g transform="matrix(1.591063,0,0,1.591063,-453.137462,-312.453167)">
                      <path className="logo-yellow" d="M747.364,653.006C730.643,641.951 723.591,623.684 728.896,605.883C734.477,587.152 750.223,575.264 769.976,574.869C770.972,574.85 771.968,574.827 773.409,574.749C777.672,576.022 781.533,577.238 785.298,578.703C796.445,583.04 803.988,591.203 809.647,602.241C812.345,612.549 813.005,622.273 809.991,631.998C809.991,631.998 810,632 809.749,632.162C809.329,632.884 809.159,633.444 808.989,634.004C808.989,634.004 809.001,634 808.754,634.163C808.333,634.886 808.161,635.446 807.988,636.006C807.988,636.006 808.002,636.001 807.755,636.164C807.334,636.887 807.161,637.447 806.987,638.007C806.987,638.007 807.002,638.001 806.755,638.163C806.334,638.885 806.161,639.445 805.988,640.005C805.988,640.005 806,640.002 805.775,640.102C805.273,640.406 805.086,640.674 804.987,641.005C804.987,641.005 805,641 804.755,641.164C804.337,641.886 804.163,642.444 803.989,643.002C803.989,643.002 804,643 803.775,643.099C803.272,643.402 803.085,643.669 802.99,644.001C802.99,644.001 803,644 802.776,644.102C802.275,644.407 802.087,644.674 801.988,645.004C801.988,645.004 802,645 801.733,645.114C800.973,645.818 800.481,646.408 799.989,646.998C799.989,646.998 800,647 799.731,647.114C798.971,647.819 798.48,648.408 797.989,648.998C797.989,648.998 798,649 797.68,649.039C796.904,649.715 796.449,650.353 795.994,650.99C795.994,650.99 796,651 795.696,651.007C794.927,651.34 794.461,651.666 793.995,651.993C793.995,651.993 794,652 793.773,652.099C793.262,652.394 793.078,652.66 792.994,652.995C792.994,652.995 793,653 792.698,653.009C791.93,653.343 791.463,653.667 790.997,653.992C790.997,653.992 791,654 790.698,654.013C789.926,654.35 789.457,654.674 788.989,654.999C788.989,654.999 789,655 788.611,655.002C779.462,657.98 770.662,660.407 761.274,658.019C758.913,657.419 756.425,657.319 753.995,656.988C753.995,656.988 753.999,657.002 753.842,656.749C753.121,656.326 752.558,656.157 751.995,655.988C751.995,655.988 751.998,656.002 751.898,655.778C751.59,655.28 751.321,655.092 750.993,654.989C750.993,654.989 751,655 750.833,654.759C750.11,654.342 749.554,654.166 748.998,653.99C748.549,653.671 748.1,653.352 747.364,653.006Z"/>
                    </g>
                  </g>
                </svg>
              </div>
              <div className="social-media-container">
                <a 
                  href="https://www.instagram.com/luchtvanheerde" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Volg ons op Instagram - @luchtvanheerde"
                  onClick={() => trackSocialClick('instagram')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="white" role="img" aria-label="Instagram icoon">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.youtube.com/@luchtvanheerde" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Bekijk onze video's op YouTube - @luchtvanheerde"
                  onClick={() => trackSocialClick('youtube')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="white" role="img" aria-label="YouTube icoon">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>                
              </div>
              <p className="hero-tagline">
                Dronefoto's en video's van Heerde en omgeving, met beelden van landschap,
                dorp en kleine details vanuit de lucht.
              </p>
              <div className="button-container">
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  className="scroll-button"
                  onClick={() => scrollToSection('achtergrond')}
                >
                  Achtergrond
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  className="scroll-button"
                  onClick={() => scrollToSection('fotogalerij')}
                >
                  Fotogalerij
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  className="scroll-button"
                  onClick={() => scrollToSection('privacy-respect')}
                >
                  Privacy & Respect
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  className="scroll-button"
                  onClick={() => scrollToSection('samenwerking')}
                >
                  Samenwerking
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </header>

      <main>
      {/* Achtergrond Section */}
      <section id="achtergrond" className="content-section background-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2>Achtergrond</h2>
              <p>
                Lucht van Heerde is ontstaan uit liefde voor Heerde en de omgeving. Als hobby 
                dronevlieger leg ik het landschap, het dorp en kleine details vast zoals ik ze zie: 
                rustig, met aandacht en zonder agenda.
              </p>
              <p>
                De beelden deel ik graag, gewoon om te laten zien hoe mooi en bijzonder de omgeving is.
              </p>
              <p>
                De meeste foto's en video's verschijnen op Instagram. Volg @luchtvanheerde ook op Instagram 
                via <a href="https://www.instagram.com/luchtvanheerde" target="_blank" rel="noopener noreferrer">@luchtvanheerde</a>.
              </p>
              <div className="back-to-top-container">
                <Button 
                  variant="outline-dark" 
                  onClick={() => scrollToTop()}
                  className="back-to-top-button"
                >
                  ↑ Terug naar boven
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Fotogalerij Section */}
      <section id="fotogalerij" className="content-section gallery-section">
        <Slideshow />
        <div className="back-to-top-container gallery-overlay-actions">
          <Button
            variant="outline-dark"
            onClick={() => scrollToTop()}
            className="back-to-top-button"
          >
            ↑ Terug naar boven
          </Button>
        </div>
      </section>

      {/* Privacy en Respect Section */}
      <section id="privacy-respect" className="content-section privacy-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2>Privacy & respect</h2>
              <p>
                Ik maak dronefoto's en video's van Heerde en omgeving, met respect voor no-fly zones, 
                rust en privacy.
              </p>
              <p>
                Communicatie, zoals reacties, likes en privéberichten, verloopt via Instagram en via het contactformulier op 
                deze website. Voor Instagram geldt het privacybeleid van Instagram zelf.
              </p>
              <p>
                Herken je jezelf, je woning of een situatie in een foto of video, of ervaar je overlast? 
                Neem dan gerust contact op via een privébericht (DM) op Instagram: @luchtvanheerde, of via het contactformulier. 
                Meldingen worden serieus genomen en respectvol behandeld.
              </p>              
              <p>
                Deze pagina is bedoeld om transparant te zijn en duidelijk te maken dat respect voor 
                omgeving en privacy altijd voorop staat.
              </p>
              <div className="back-to-top-container">
                <Button 
                  variant="outline-dark" 
                  onClick={() => scrollToTop()}
                  className="back-to-top-button"
                >
                  ↑ Terug naar boven
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Samenwerking Section */}
      <section id="samenwerking" className="content-section collaboration-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2>Samenwerking</h2>
              <p>
                Alle beelden zijn door mij gemaakt, zonder commercieel doel. 
                Gebruik van beelden gebeurt in overleg en met bronvermelding.
              </p>
              <p>
                Ik sta open voor ideeën, vragen of samenwerkingen die passen bij het karakter: 
                lokaal, rustig en zonder agenda. Gebruik van beeldmateriaal is mogelijk in overleg 
                en bij voorkeur met bronvermelding (@luchtvanheerde).
              </p>              
              <p>
                Twijfel je of iets mag, of wil je een beeld gebruiken? Neem dan gerust contact op via 
                een privébericht (DM) op Instagram: @luchtvanheerde. Alles gebeurt op basis van wederzijds 
                respect en transparantie.
              </p>
              <p>
                Liever geen DM? Je kunt ook contact opnemen via het contactformulier.
              </p>
              <div className="contact-cta-container">
                <Button
                  type="button"
                  variant="dark"
                  className="contact-cta-button"
                  onClick={() => setShowContactModal(true)}
                >
                  Contactformulier
                </Button>
              </div>
              <div className="back-to-top-container">
                <Button 
                  variant="outline-dark" 
                  onClick={() => scrollToTop()}
                  className="back-to-top-button"
                >
                  ↑ Terug naar boven
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      </main>

      <footer className="site-footer" aria-label="Footer">
        <Container>
          <p>
            © {new Date().getFullYear()} Lucht van Heerde ·
            {' '}
            <a href="https://www.instagram.com/luchtvanheerde" target="_blank" rel="noopener noreferrer">Instagram</a>
            {' '}
            ·
            {' '}
            <a href="https://www.youtube.com/@luchtvanheerde" target="_blank" rel="noopener noreferrer">YouTube</a>
            {' '}
            ·
            {' '}
            <button type="button" className="footer-contact-button" onClick={() => setShowContactModal(true)}>
              Contactformulier
            </button>
          </p>
        </Container>
      </footer>

      <Modal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        centered
        className="contact-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Contactformulier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="contact-modal-intro">
            Vul het formulier in. Liever direct een DM? Gebruik dan
            {' '}
            <a href="https://www.instagram.com/luchtvanheerde" target="_blank" rel="noopener noreferrer">
              Instagram
            </a>
            .
          </p>
          <ContactForm />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default App;
